import React, { Component, Fragment, ReactNode } from 'react';
import router, {
  IRouteChangeCompletedData,
  IRouteChangeStartedData,
  RouterEvent,
} from './router';
import matchRoute from './utils/matchRoute';

import getInitialProps from './getInitialProps';

import { IRoutePage, IRoutePageContext } from './types';

interface IBrowserRouterProps {
  context: IRoutePageContext;
  routes: IRoutePage[];
  initialProps: any;
  allowCacheProps?: boolean;
  ssr?: boolean;
  children: (value) => ReactNode;
}

interface IBrowserRouterState {
  isLoading: boolean;
  isInitialed: boolean;
  routerState?: string;
  route?;
  routeProps?: any;
}

class BrowserRouter extends Component<
  IBrowserRouterProps,
  IBrowserRouterState
> {
  private _unsubscribe: Function;
  private _propsByRoute = {};

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isInitialed: false,
    };

    if (this.props.ssr) {
      const routerState = router.state;
      const routeProps = this.props.initialProps;

      Object.assign(this.state, {
        isInitialed: true,
        routerState,
        route: matchRoute(this.props.routes, routerState),
        routeProps,
      });

      if (routeProps && !routeProps.error) {
        this._propsByRoute[routerState] = routeProps;
      }
    }
  }

  getInitialProps(routerState, route) {
    const { allowCacheProps, context } = this.props;

    // TODO: cache routeProps or not? context could be changed
    if (allowCacheProps && this._propsByRoute[routerState]) {
      return Promise.resolve(this._propsByRoute[routerState]);
    }

    const { component, params } = route;

    return getInitialProps(component, { params, context }).then((props) => {
      if (allowCacheProps && !props.error) {
        this._propsByRoute[routerState] = props;
      }

      return props;
    });
  }

  componentDidMount() {
    const { routes } = this.props;

    const updateRouteState = () => {
      if (this.state.routerState !== router.state) {
        const { isInitialed } = this.state;
        const routerState = router.state;
        const route = matchRoute(routes, routerState);

        if (!route) {
          router.emit(RouterEvent.ROUTE_NOTE_FOUND, {
            routerState,
            route,
          });

          this.setState({
            routerState,
            route: null,
          });
          return;
        }

        if (isInitialed) {
          router.emit<IRouteChangeStartedData>(
            RouterEvent.ROUTE_CHANGE_STARTED,
            {
              routerState,
              route,
            },
          );
        }

        this.setState({ isLoading: true });
        this.getInitialProps(routerState, route).then((routeProps) => {
          if (routerState !== router.state) {
            return;
          }

          if (isInitialed) {
            router.emit<IRouteChangeCompletedData>(
              RouterEvent.ROUTE_CHANGE_COMPLETED,
              {
                routerState,
                route,
                routeProps,
                routeError: routeProps.error,
              },
            );
          }

          this.setState({
            isLoading: false,
            isInitialed: true,
            routerState,
            route,
            routeProps,
          });
        });
      }
    };

    this._unsubscribe = router.subscribeState(updateRouteState);

    // https://reactjs.org/blog/2018/03/27/update-on-async-rendering.html#adding-event-listeners-or-subscriptions
    // External values could change between render and mount
    updateRouteState();
  }

  componentWillUnmount() {
    this._unsubscribe();
  }

  render() {
    const { isLoading, route, routeProps } = this.state;
    const { children } = this.props;

    return (
      <Fragment>
        {children({
          isLoading,
          Component: route && route.component,
          props: routeProps,
        })}
      </Fragment>
    );
  }
}

export default BrowserRouter;
